"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Eye, Brain, Zap } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function ThothMajorArcanaPage() {
  const majorArcanaCards = [
    { number: "0", name: "The Fool", description: "Saf potansiyel ve yolculuğun başlangıcı", path: "the-fool" },
    { number: "I", name: "The Magus", description: "İrade gücü ve manifestasyon", path: "the-magus" },
    { number: "II", name: "The Priestess", description: "Sezgi ve gizli bilgi", path: "the-priestess" },
    { number: "III", name: "The Empress", description: "Yaratıcılık ve bereket", path: "the-empress" },
    { number: "IV", name: "The Emperor", description: "Otorite ve yapı", path: "the-emperor" },
    { number: "V", name: "The Hierophant", description: "Ruhsal öğretim ve gelenek", path: "the-hierophant" },
    { number: "VI", name: "The Lovers", description: "Seçim ve birleşme", path: "the-lovers" },
    { number: "VII", name: "The Chariot", description: "Zafer ve kontrol", path: "the-chariot" },
    { number: "VIII", name: "Adjustment", description: "Denge ve adalet", path: "adjustment" },
    { number: "IX", name: "The Hermit", description: "İç arayış ve bilgelik", path: "the-hermit" },
    { number: "X", name: "Fortune", description: "Kader ve döngüler", path: "fortune" },
    { number: "XI", name: "Lust", description: "Yaşam gücü ve tutku", path: "lust" },
    { number: "XII", name: "The Hanged Man", description: "Fedakarlık ve yeni bakış açısı", path: "the-hanged-man" },
    { number: "XIII", name: "Death", description: "Dönüşüm ve yeniden doğuş", path: "death" },
    { number: "XIV", name: "Art", description: "Yaratıcı sentez ve uyum", path: "art" },
    { number: "XV", name: "The Devil", description: "Maddi dünya ve kısıtlamalar", path: "the-devil" },
    { number: "XVI", name: "The Tower", description: "Ani aydınlanma ve yıkım", path: "the-tower" },
    { number: "XVII", name: "The Star", description: "Umut ve ilham", path: "the-star" },
    { number: "XVIII", name: "The Moon", description: "İllüzyon ve bilinçsiz", path: "the-moon" },
    { number: "XIX", name: "The Sun", description: "Aydınlanma ve başarı", path: "the-sun" },
    { number: "XX", name: "The Aeon", description: "Yeni çağ ve yargı", path: "the-aeon" },
    { number: "XXI", name: "The Universe", description: "Kozmik birlik ve tamamlanma", path: "the-universe" }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-indigo-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-indigo-500">/</span>
            <span className="text-sm">Thoth</span>
            <span className="text-indigo-500">/</span>
            <span className="text-sm">Majör Arkana</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/thoth"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Thoth Tarot
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Majör Arkana</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-900/20 to-purple-900/20 rounded-xl p-8 mb-8 border border-indigo-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Eye className="h-10 w-10 text-indigo-400" />
            Thoth Majör Arkana
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">
            Aleister Crowley'nin ezoterik görüşleri ve Lady Frieda Harris'in sanatsal yorumu ile yaratılan 22 Trump kartı.
            Thelema felsefesi ve Kabalistik Tree of Life'ın yollarını temsil eden derin sembolik yapı.
          </p>
        </div>

        {/* Thoth Specificities */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Thoth Özel Özellikleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Brain className="h-5 w-5 text-indigo-400" />
                Crowley'nin Değişiklikleri
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Strength → Lust (XI)</li>
                <li>• Temperance → Art (XIV)</li>
                <li>• Judgement → The Aeon (XX)</li>
                <li>• The World → The Universe (XXI)</li>
                <li>• Justice → Adjustment (VIII)</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-400" />
                Thelema Felsefesi
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• "Do what thou wilt" prensibi</li>
                <li>• Tree of Life'ın 22 yolu</li>
                <li>• Kabalistik correspondence'lar</li>
                <li>• Astrolojik ve elementel bağlantılar</li>
                <li>• Mısır mitolojisi etkileri</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">22 Thoth Trump Kartı</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {majorArcanaCards.map((card, index) => (
              <Link
                key={index}
                href={`/about/tarot/systems/thoth/major-arcana/${card.path}`}
                className="group bg-slate-800 rounded-xl p-4 border border-slate-700 hover:border-indigo-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/10"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-32 mb-3 rounded-lg overflow-hidden bg-gradient-to-b from-indigo-900/20 to-slate-900 border border-indigo-600/20 group-hover:scale-105 transition-transform duration-300">
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={`/images/tarot/cards/thoth-${card.path}.svg`}
                        alt={card.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          if (target.nextElementSibling) {
                            (target.nextElementSibling as HTMLElement).style.display = 'flex';
                          }
                        }}
                      />
                      <div className="hidden w-full h-full flex-col items-center justify-center text-indigo-300">
                        <span className="text-lg font-bold">{card.number}</span>
                        <span className="text-xs text-center px-1">{card.name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="text-lg font-semibold text-slate-100 mb-1">{card.name}</div>
                    <div className="text-sm text-indigo-400 mb-1">({card.number})</div>
                    <div className="text-xs text-slate-400">{card.description}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları Thoth Tarot'un geleneksel yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/thoth"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Thoth Tarot
          </Link>

          <Link
            href="/about/tarot/systems/thoth/minor-arcana"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
            Minör Arkana
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}